import { useState } from 'react';
import { useReviewSummary, useSetAutoclose } from '../api/readingReview.ts';
import {
  COMMISSION_EVIDENCE, TOGGLE_HEADING, TOGGLE_LINE, TURN_OFF, TURN_ON, WHY_NOW,
  toggleLine, yardstick, type KindCode,
} from '../foundInDocuments.ts';

const button = 'inline-flex min-h-[44px] items-center rounded-lg border border-neutral-300 bg-panel px-4 text-sm font-semibold text-neutral-900 hover:bg-neutral-50';

/**
 * Section R (176; A-#608 5): "Closing by rule", in Company policies beside the ASAP window, as the cleared words place
 * it. One line per kind: on since when and by whom, or off, or "a person always records this"; the yardstick beside
 * each reading kind; the 14 percent line beside the commission one. Only the System Administrator gets the buttons
 * (the server decides: reading_autoclose_set() refuses anyone else); any other reader of Company policies sees the
 * lines read only. "Found in the documents" links here.
 */
export function ClosingByRule() {
  const summary = useReviewSummary();
  const set = useSetAutoclose();
  const [reason, setReason] = useState('');
  if (!summary.data) return null;   // a reader the review gate refuses sees nothing here, as on the page
  const { kinds, yardstick: yard, may_set_toggle } = summary.data;
  return (
    <section aria-label={TOGGLE_HEADING} className="border-t border-neutral-200 px-4 py-4 lg:px-5">
      <h2 className="text-base font-semibold text-neutral-900">{TOGGLE_HEADING}</h2>
      <p className="max-w-[60rem] text-sm text-neutral-600">{TOGGLE_LINE}</p>
      {may_set_toggle ? (
        <label className="mt-2 block text-sm">{WHY_NOW}
          <input type="text" maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)}
                 className="ml-2 min-h-[44px] w-full max-w-[40rem] rounded border border-neutral-300 bg-panel px-2" />
        </label>
      ) : null}
      <ul className="mt-2 space-y-1">
        {kinds.filter((k) => k.basis !== 'list').map((k) => {
          const y = k.code === 'P_OATH' ? yard.P_OATH : k.code === 'P_COMMISSION' ? yard.P_COMMISSION : null;
          const line = toggleLine(k);
          return (
            <li key={k.code} className="flex flex-wrap items-center gap-3 text-sm">
              <span>{line}</span>
              {y ? <span className="text-neutral-700">{yardstick(y.agrees, y.with_paper)}</span> : null}
              {k.code === 'P_COMMISSION' ? <span className="text-neutral-700">{COMMISSION_EVIDENCE}</span> : null}
              {may_set_toggle && k.may_auto ? (
                <button type="button" className={button} disabled={set.isPending}
                        onClick={() => set.mutate({ kind: k.code as KindCode, enabled: !k.on, reason: k.on ? null : reason })}>
                  {k.on ? TURN_OFF : TURN_ON}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {set.error ? <p role="alert" className="mt-2 text-sm text-critical-edge">{set.error.message}</p> : null}
    </section>
  );
}
